/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, memo, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Field, Combobox, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useWorkItemFieldHook } from './use-copy-field-value';
import { useAzureProvider } from 'src/azure/context';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import { useAutomationRuleActions } from '../../use-actions';
import { editWorkItemStyles } from '../styles';

export interface Props {
  actionId: string;
  validationError: string | null;
}

export interface CopyFieldValueInputRef {
	updateOptions: (input: { workItemField: string; projectId: string }) => Promise<void>;
}

export const useCopyFieldValueInputRef = () => useRef<CopyFieldValueInputRef>(null);

function CopyFieldValueInputRef(props: Props, ref: Ref<CopyFieldValueInputRef>) {
	const classes = editWorkItemStyles();
	const {applicationContext} = useAzureProvider();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const workItemFieldHook = useWorkItemFieldHook();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundActions = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundActions!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);
  
	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
	
		if (value === '') {
			workItemFieldHook
				.getWorkItemFieldOptions({
					projectId: applicationContext.projectId,
					selectedFieldValue: current.workItemField,
				})
				.then((resultOptions) => {
					setOptions(resultOptions);
				});
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options, applicationContext.projectId]);

	const setWorkItemField = useCallback((value: string) => {
		resetValidationErrors();
		if (value) {
			setStatus('selected');
			const foundAction = actions.find(item => item.id === props.actionId);
	
			if (foundAction) {
				setActions(oldActions => {
					const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
					const others = oldActions.filter(item => item.id !== props.actionId);
	
					return [...others, {
						...current, editWorkItem: {
							...current.editWorkItem as EditWorkItemActionType, 
							copyFieldValue: value
						}
					}];
				});
			}
		}
	}, []);

	const loadOptions = useCallback(async () => {
		const resultOptions = await workItemFieldHook.getWorkItemFieldOptions({
			projectId: applicationContext.projectId,
			selectedFieldValue: current.workItemField,
		});
		setOptions(resultOptions);
	}, [current.workItemField, applicationContext.projectId]); 

	const valueRender = useMemo(() => {
		if (current && current.copyFieldValue && status === 'selected') {
			return options.find(item => item.value === current.copyFieldValue)?.label || '';
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current.copyFieldValue, options, status]);

	const selectedOptions = useMemo(() => {
		const found = options.find(item => item.value === current.copyFieldValue);

		if (current.copyFieldValue && found) {
			return [found.value];
		}

		return [];
	}, [current.copyFieldValue, options]);

	useImperativeHandle(ref, () => ({
		updateOptions: async (input: { workItemField: string; projectId: string }) => {
			setOnInitialLoading(true);
			const resultOptions = await workItemFieldHook.getWorkItemFieldOptions({
				projectId: input.projectId,
				selectedFieldValue: input.workItemField,
			});
			setOptions(resultOptions);
			setOnInitialLoading(false);
		}
	}));

	useEffect(() => {
		setOnInitialLoading(true);
		const foundAction = actions.find(item => item.id === props.actionId);

		const asyncInit = async () => {
			if (foundAction && foundAction?.editWorkItem) {
				await loadOptions();
			}

			setOnInitialLoading(false);
		};

		asyncInit();
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				id={`action-${props.actionId}-copy-field-value`}
				className={classes.dropdown}
				autoComplete='off'
				appearance='outline'
				placeholder='Choose a field'
				positioning='below-start'
				clearable
				value={valueRender}
				selectedOptions={selectedOptions}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onOptionSelect={(_, data) => {
					setWorkItemField(data.optionValue as string);
				}}
				onBlur={() => {
					loadOptions()
						.then(() => {
							setStatus('selected');
						});
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}

const CopyFieldValueInput = CopyFieldValueInputRef;
export default memo(forwardRef(CopyFieldValueInput));